import React, { useCallback, useState } from 'react';
import TenderFormModal from '../components/tenders/TenderFormModal';
import { tenderService, CreateTenderData } from '../services/tenderService';
import { Tender } from '../types';
import { useAppData } from '../context/AppDataContext';
import {
  buildCreateTenderDataFromTender,
  emptyTenderFormData,
} from '../utils/tenderFormUtils';

export function useTenderEditModal(onSaved?: () => void | Promise<void>) {
  const { refreshUsers, refreshBrands } = useAppData();
  const [showModal, setShowModal] = useState(false);
  const [editingTender, setEditingTender] = useState<Tender | null>(null);
  const [formData, setFormData] = useState<CreateTenderData>(emptyTenderFormData);
  const [selectedAwardeeName, setSelectedAwardeeName] = useState('');
  const [error, setError] = useState('');

  const openEdit = useCallback(
    async (tenderRow: Tender) => {
      try {
        setError('');
        await Promise.all([refreshUsers(), refreshBrands()]);
        const fullTender = await tenderService.getById(tenderRow.id);
        setEditingTender(fullTender);
        const { formData: fd, awardeeName } = buildCreateTenderDataFromTender(fullTender, tenderRow);
        setFormData(fd);
        setSelectedAwardeeName(awardeeName);
        setShowModal(true);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        setError(msg || 'Failed to load tender');
      }
    },
    [refreshUsers, refreshBrands]
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingTender(null);
    setError('');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTender) {
      return;
    }
    setError('');
    const validAssignments = (formData.assignments || []).filter((a) => a.user_id && a.user_id > 0);
    if (validAssignments.length === 0) {
      setError('At least one assignment (brand and user) is required');
      return;
    }
    if (!formData.awardee_id) {
      setError('Please select an awardee');
      return;
    }
    const payload = {
      ...formData,
      assignments: validAssignments,
      awardee_email: formData.awardee_email?.trim() ? formData.awardee_email.trim() : null,
    };
    try {
      await tenderService.update(editingTender.id, payload);
      setShowModal(false);
      setEditingTender(null);
      await onSaved?.();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(msg || 'Failed to save data');
    }
  };

  const editModal = (
    <TenderFormModal
      isOpen={showModal}
      editingTender={editingTender}
      formData={formData}
      setFormData={setFormData}
      selectedAwardeeName={selectedAwardeeName}
      setSelectedAwardeeName={setSelectedAwardeeName}
      error={error}
      onClose={closeModal}
      onSubmit={handleSubmit}
    />
  );

  return { openEdit, editModal, error, setError };
}
